import React, { useState, useEffect } from 'react';
import { Cloud } from 'lucide-react';
import { MARKET_STATS } from '@/data/marketData';
import { fetchLatestMarketUpdateInfo, MarketUpdateInfo } from '@/lib/marketUpdateTime';

interface HeroSectionProps {
  lang: 'en' | 'ta';
}

export default function HeroSection({ lang }: HeroSectionProps) {
  const stats = MARKET_STATS;
  const [updateInfo, setUpdateInfo] = useState<MarketUpdateInfo | null>(null);

  useEffect(() => {
    fetchLatestMarketUpdateInfo().then(setUpdateInfo);
  }, []);

  const displayDate = updateInfo
    ? (lang === 'en' ? updateInfo.formattedDate : updateInfo.formattedDateTa)
    : '';

  const displayTime = updateInfo ? updateInfo.formattedTime : '';

  const isPending = updateInfo?.isPendingToday ?? false;

  return (
    <section className="hero-gradient rounded-4xl p-6 md:p-10 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, var(--green-mid), transparent)' }} />
      <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, var(--accent), transparent)' }} />

      <div className="relative z-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${stats.marketStatus === 'open' ? 'bg-drop-green text-white' : 'bg-muted text-muted-foreground'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${stats.marketStatus === 'open' ? 'bg-white animate-pulse' : 'bg-muted-foreground'}`} />
                {stats.marketStatus === 'open'
                  ? (lang === 'en' ? 'Market Open' : 'சந்தை திறந்துள்ளது')
                  : (lang === 'en' ? 'Market Closed' : 'சந்தை மூடப்பட்டுள்ளது')}
              </span>
              {updateInfo && (
                <span className="text-xs text-muted-foreground">
                  {lang === 'en' ? 'Updated' : 'புதுப்பிக்கப்பட்டது'} {displayTime}
                </span>
              )}
              {isPending && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {lang === 'en' ? 'Pending Today\'s Update' : 'இன்றைய புதுப்பிப்பு நிலுவையில்'}
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              {lang === 'en' ? "Today's Market" : 'இன்றைய சந்தை'}
              <br />
              <span className="text-primary">
                {lang === 'en' ? 'Koyambedu, Chennai' : 'கோயம்பேடு, சென்னை'}
              </span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {displayDate}
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card/80 rounded-2xl px-4 py-2.5 border border-border">
            <Cloud size={16} className="text-blue-400" />
            <span>{lang === 'en' ? stats.weatherToday : stats.weatherTodayTa}</span>
          </div>
        </div>

        {/* Snapshot stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            {
              id: 'stat-products',
              label: lang === 'en' ? 'Products Tracked' : 'கண்காணிக்கப்படுகின்றன',
              value: stats.totalProducts.toString(),
              icon: '🌿',
              sub: lang === 'en' ? 'across 5 categories' : '5 வகைகளில்',
            },
            {
              id: 'stat-updated',
              label: lang === 'en' ? 'Updated Today' : 'இன்று புதுப்பிக்கப்பட்டது',
              value: stats.updatedToday.toString(),
              icon: '✅',
              sub: lang === 'en' ? 'prices live' : 'விலைகள் நேரடி',
            },
            {
              id: 'stat-drop',
              label: lang === 'en' ? 'Biggest Drop' : 'அதிக வீழ்ச்சி',
              value: `₹${stats.biggestDrop.price}`,
              icon: '📉',
              sub: `${stats.biggestDrop.product} · ${stats.biggestDrop.change}%`,
              accent: 'text-drop-green',
            },
            {
              id: 'stat-rise',
              label: lang === 'en' ? 'Biggest Rise' : 'அதிக உயர்வு',
              value: `₹${stats.biggestRise.price}`,
              icon: '📈',
              sub: `${stats.biggestRise.product} · +${stats.biggestRise.change}%`,
              accent: 'text-rise-red',
            },
          ].map(stat => (
            <div key={stat.id} className="glass-card rounded-3xl p-4 md:p-5">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <p className="section-label mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold font-tabular ${stat.accent || 'text-foreground'}`}>
                {stat.value}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}